<template>
  <div class="basic-profile-wrapper pa-2">
    <!-- Profile Header Card -->
    <v-card outlined class="rounded-lg mb-4 pa-5">
      <div class="d-flex flex-column flex-md-row align-start align-md-center">
        <!-- Avatar Pic -->
        <div class="mr-6 mb-3 mb-md-0 position-relative">
          <v-img
            class="profile-image"
            width="100"
            height="100"
            :lazy-src="$defaultProfileImage"
            :src="getProfileImage"
            alt="Profile Image"
          ></v-img>
        </div>

        <!-- Info Header -->
        <div class="flex-grow-1">
          <div class="d-flex align-center flex-wrap mb-2">
            <h2
              class="text-h5 font-weight-bold grey--text text--darken-3 mr-3 mb-0"
            >
              {{ currentUser.full_name }}
            </h2>
            <v-chip
              v-if="currentUser.activated"
              color="success"
              small
              label
              class="font-weight-medium px-3"
              v-tippy="{ content: 'Account is activated', arrow: true }"
            >
              <v-icon left x-small>mdi-check-circle</v-icon>
              Active
            </v-chip>
          </div>

          <!-- Contact Details Grid -->
          <div class="d-flex flex-wrap align-center">
            <div
              class="d-flex align-center mr-6 mb-2 text-subtitle-2 grey--text text--darken-1"
            >
              <v-icon small color="cyan" class="mr-2">mdi-email-outline</v-icon>
              <a
                :href="'mailto:' + currentUser.user_email"
                class="text-decoration-none cyan--text text--darken-1 font-weight-medium"
              >
                {{ currentUser.user_email }}
              </a>
            </div>

            <div
              class="d-flex align-center mr-6 mb-2 text-subtitle-1 grey--text text--darken-1"
            >
              <v-icon small color="cyan" class="mr-2"
                >mdi-account-outline</v-icon
              >
              <span>{{ currentUser.user_name }}</span>
            </div>

            <div
              v-if="currentUser.phone_number"
              class="d-flex align-center mr-6 mb-2 text-subtitle-1 grey--text text--darken-1"
            >
              <v-icon small color="cyan" class="mr-2">mdi-phone-outline</v-icon>
              <a
                :href="'tel:' + currentUser.phone_number"
                class="text-decoration-none grey--text text--darken-2"
              >
                {{ currentUser.phone_number }}
              </a>
            </div>

            <div v-if="currentUser.role_name" class="d-flex align-center mb-2">
              <v-chip
                color="cyan"
                outlined
                small
                class="font-weight-bold text-uppercase px-3"
              >
                <v-icon left x-small>mdi-shield-account-outline</v-icon>
                {{ currentUser.role_name }}
              </v-chip>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Main Tabs Section -->
    <v-card outlined class="rounded-lg px-4">
      <v-tabs
        v-model="userTab"
        background-color="transparent"
        color="cyan"
        active-class="font-weight-bold cyan--text"
        slider-color="cyan"
        class="border-bottom mb-3"
      >
        <v-tab
          v-for="(tab, index) in tabs"
          :key="index"
          :href="'#tab-' + tab.key"
          :disabled="tab.disabled"
          class="text-capitalize font-weight-medium px-6"
        >
          <v-icon small left class="mr-2">
            {{
              tab.key === "history"
                ? "mdi-history"
                : tab.key === "signature"
                ? "mdi-draw"
                : tab.key === "login-log"
                ? "mdi-format-list-bulleted-type"
                : "mdi-lock-outline"
            }}
          </v-icon>
          {{ tab.title }}
        </v-tab>
      </v-tabs>

      <perfect-scrollbar
        :options="{ suppressScrollX: true }"
        class="scroll"
        style="height: calc(100vh - 280px); position: relative"
      >
        <v-tabs-items v-model="userTab" touchless>
          <v-tab-item :value="'tab-history'">
            <RecentActivity :user="userId"></RecentActivity>
          </v-tab-item>

          <v-tab-item value="tab-signature">
            <v-container class="pa-2">
              <v-row>
                <!-- Upload Signature Card -->
                <v-col cols="12" md="6">
                  <v-card
                    outlined
                    class="pa-4 fill-height d-flex flex-column align-center justify-space-between rounded-lg"
                  >
                    <div
                      class="subtitle-1 font-weight-bold grey--text text--darken-2 align-self-start mb-2"
                    >
                      <v-icon small class="mr-1" color="cyan"
                        >mdi-image-outline</v-icon
                      >
                      Uploaded Signature
                    </div>
                    <div
                      class="d-flex align-center justify-center rounded pa-2 grey lighten-4 width-100 mb-3"
                      style="
                        width: 100%;
                        min-height: 130px;
                        border: 1px dashed #cfd8dc;
                      "
                    >
                      <v-img
                        contain
                        v-if="attachment_url"
                        :key="attachment_url"
                        :lazy-src="$defaultImage"
                        :src="attachment_url"
                        max-height="110"
                        max-width="240"
                      ></v-img>
                      <v-img
                        v-else
                        contain
                        :lazy-src="$defaultImage"
                        :src="$defaultImage"
                        max-height="110"
                        max-width="240"
                      ></v-img>
                    </div>
                    <v-btn
                      v-on:click="select_file()"
                      color="cyan"
                      dark
                      depressed
                      class="custom-bold-button text-capitalize"
                    >
                      <v-icon left small>mdi-cloud-upload-outline</v-icon>
                      Change Image
                    </v-btn>
                    <v-file-input
                      ref="select-file"
                      class="d-none"
                      accept="image/png, image/jpeg, image/jpg"
                      v-model="signature"
                      dense
                      v-on:change="on_file_change($event)"
                      hide-details
                    ></v-file-input>
                  </v-card>
                </v-col>

                <!-- Draw Signature Card -->
                <v-col cols="12" md="6">
                  <v-card
                    outlined
                    class="pa-4 fill-height d-flex flex-column align-center justify-space-between rounded-lg"
                  >
                    <div
                      class="d-flex align-center justify-space-between width-100 mb-2"
                      style="width: 100%"
                    >
                      <div
                        class="subtitle-1 font-weight-bold grey--text text--darken-2"
                      >
                        <v-icon small class="mr-1" color="cyan"
                          >mdi-draw</v-icon
                        >
                        Draw Signature
                      </div>
                      <v-btn
                        content="Click here to clear signature"
                        v-tippy="{ arrow: true, placement: 'top' }"
                        icon
                        small
                        color="error"
                        v-on:click="customer_signature.clear()"
                      >
                        <v-icon small>mdi-close-circle-outline</v-icon>
                      </v-btn>
                    </div>
                    <div
                      class="signature-pad d-flex justify-center my-auto"
                      id="signature-pad"
                    >
                      <div
                        ref="customer_signature_div"
                        style="width: 400px"
                        class="custom-border-grey-dashed signature-pad-wrap rounded"
                      >
                        <canvas
                          ref="customer_signature"
                          style="width: 400px; height: 120px !important"
                        ></canvas>
                      </div>
                    </div>
                    <div class="text-center mt-2">
                      <v-btn
                        v-on:click="submitSignature"
                        color="cyan"
                        depressed
                        class="custom-bold-button text-capitalize px-6 white--text"
                        :disabled="valid_signature"
                      >
                        <v-icon left small>mdi-check-circle-outline</v-icon>
                        Submit Draw Signature
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>

          <v-tab-item :value="'tab-password'">
            <PasswordUpdate
              isOldPassword
              :user="userId"
              v-on:password:updated="logoutUser"
            ></PasswordUpdate>
          </v-tab-item>
          <v-tab-item :value="'tab-login-log'">
            <LoginLog :user="userId" :logs="loginLogs"></LoginLog>
          </v-tab-item>
        </v-tabs-items>
      </perfect-scrollbar>
    </v-card>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import LoginLog from "@/view/pages/user/detail/LoginLog";
import PasswordUpdate from "@/view/pages/user/detail/PasswordUpdate";
import RecentActivity from "@/view/pages/user/detail/RecentActivity";
//import { GET } from "@/core/services/store/request.module";
import { GET, PATCH, UPLOAD, PUT } from "@/core/services/store/request.module";
import JwtService from "@/core/services/jwt.service";
import SignaturePad from "signature_pad";
import ObjectPath from "object-path";

export default {
  name: "basic-profile",
  data() {
    return {
      userId: 0,
      userTab: "tab-signature",
      pageLoading: true,
      currentUser: new Object(),
      userData: new Object(),
      loginLogs: [],
      signature: null,
      customer_signature: null,
      attachment_url: null,
      tabs: [
        {
          title: "Recent Activities",
          icon: "mdi-credit-card",
          key: "history",
          disabled: false,
        },
        {
          title: "Signature",
          icon: "mdi-credit-card",
          key: "signature",
          disabled: false,
        },
        {
          title: "Login Log",
          icon: "mdi-credit-card",
          key: "login-log",
          disabled: false,
        },
        {
          title: "Password",
          icon: "mdi-credit-card",
          key: "password",
          disabled: false,
        },
      ],
    };
  },
  components: {
    LoginLog,
    PasswordUpdate,
    RecentActivity,
  },
  methods: {
    select_file() {
      this.$refs["select-file"].$refs["input"].click();
    },
    on_file_change(file) {
      if (file) {
        this.attachment_url = URL.createObjectURL(file);
        let formData = new FormData();
        formData.append("files[0]", file, file.name);
        this.$store
          .dispatch(UPLOAD, { url: "file-manager/upload", data: formData })
          .then((response) => {
            this.attachment_url = ObjectPath.get(response, "data.0.file.url");
            this.$refs["select-file"].reset();
            this.update_signature();
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    update_signature() {
      this.$store
        .dispatch(PATCH, {
          url: `user/${this.userId}/signature/update`,
          data: { signature: this.attachment_url },
        })
        .catch((error) => {
          this.logError(error);
        });
    },

    submitSignature() {
      const _this = this;

      if (_this.customer_signature.isEmpty()) {
        return false;
      }
      _this.validationErrorBag = new Array();

      _this.$store
        .dispatch(PUT, {
          url: `user/${this.userId}/signature/update`,
          data: {
            customer: _this.customer_signature.toDataURL(),
          },
        })
        .then(() => {
          //this.getUser();
        })
        .catch(({ response }) => {
          if (response && response.data && response.data.message) {
            _this.validationErrorBag = response.data.message.split("<br>");
          } else {
            _this.validationErrorBag.push(
              "Something went wrong, Please try again later."
            );
          }
        })
        .finally(() => {
          _this.loadContent();
          _this.customer_signature.clear();
        });
    },

    logoutUser() {
      JwtService.destroyAuth();
    },
    getUserProfile() {
      const _this = this;
      try {
        _this.currentUser = JwtService.currentUser();
        _this.userId = _this.currentUser.id;
      } catch (error) {
        _this.logError(error);
      }
    },
    getUser() {
      const _this = this;
      return new Promise((resolve, reject) => {
        try {
          _this.$store
            .dispatch(GET, { url: "user/" + _this.userId })
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(error);
        }
      });
    },
    loadContent() {
      const _this = this;
      _this.pageLoading = true;
      _this
        .getUser()
        .then((response) => {
          _this.userData = response;
          _this.attachment_url = response.signature;
          _this.loginLogs = response.login_details || [];
        })
        .catch((error) => {
          _this.logError(error);
          _this.$router.go(-1);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },

    initSignature() {
      const _this = this;
      _this.$nextTick(() => {
        let ccanvas = _this.$refs["customer_signature"];
        let ccanvasDiv = _this.$refs["customer_signature_div"];
        // let cparentDiv = document.getElementById("signature-pad");
        let cparentWidth = 400;
        ccanvas.setAttribute("width", cparentWidth);
        ccanvasDiv.setAttribute("style", "width:" + cparentWidth + "px");
        _this.customer_signature = new SignaturePad(ccanvas);
      });
    },
  },
  mounted() {
    this.getUserProfile();
    this.loadContent();
    this.initSignature();
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Profile" },
      { title: "Basic" },
    ]);
  },
  computed: {
    getProfileImage() {
      if (
        this.lodash.isEmpty(this.currentUser) === false &&
        this.lodash.isEmpty(this.currentUser.profile_logo) === false
      ) {
        return this.currentUser.profile_logo.file.url;
      }
      return null;
    },
    valid_signature() {
      return this.customer_signature
        ? this.customer_signature.isEmpty()
        : false;
    },
  },
};
</script>
